import { useGetAiWorkersByCategoryQuery, useTypedSelector, categoryType, useDeleteAiWorkersMutation, RouteState, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import {
  CardListViewMode,
  EntityCardWithActions,
  EntityListView,
  ListActionsCellWithDelete,
  checkType,
  CtyButton,
  CtyShowToast,
  SimpleDeleteConfirmationModal,
  getActionsColumnDef,
  getDescriptionColumnDef,
  getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { Card, CardBody, Chip, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AnalystTabs } from './analystBoard';
import {
  ANALYST_RUNTIMES,
  analystRoute,
  isInsightAgentWorker,
  isInsightDashboardWorker,
  isPrivateDashboardItem,
  listedDashboardId,
  useAnalystInsight,
  type AnalystInsight,
  type AnalystWorkerLike,
} from './analystRuntime';

/** An aiworker as the list shows it. */
type ListedAgent = AnalystWorkerLike & {
  readonly description?: string;
  /** When DOC (or, for a private dashboard, its configuration) last changed it; shown on the new cards. */
  readonly updated_at?: string | null;
  readonly created_at?: string | null;
};

/** The list's stand-in for the tenant's default Insight Agent until someone customises it. */
export const DEFAULT_INSIGHT_AGENT_ITEM_ID = 'insight-agent-default';

const isDefaultStandIn = (report: ListedAgent | null | undefined) => report?.aiworker_id === DEFAULT_INSIGHT_AGENT_ITEM_ID;

function buildReportUrl(report: any, tabValue?: string): string {
  const reportID = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'report_id')?.attr_value;
  const analystType = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'analyst_type')?.attr_value;
  const reportType = report.aiworkers_attrs?.find((attr: any) => attr.attr_name === 'report_type')?.attr_value;
  const aiworkerId = report.aiworker_id;
  const tab = tabValue ?? (analystType === 'prescriptive' ? AnalystTabs.CLAIM_SCORE : AnalystTabs.DASHBOARD);
  const params = new URLSearchParams({ tab, aiworkerId });
  if (reportID) params.append('reportId', reportID);
  if (analystType === 'evaluations' || analystType === 'decision' || analystType === 'pyanalyst') {
    params.append('analystType', analystType);
    if (analystType === 'evaluations' && reportType) params.append('reportType', reportType);
  }
  return `/analytics/${RouteState.VIEW}?${params.toString()}`;
}

/** A listed dashboard opens its own page (Launch and Edit alike: its owner edits it there). */
const dashboardPage = (report: ListedAgent, insight: AnalystInsight | null): string | null =>
  (insight?.dashboardUrl && isInsightDashboardWorker(report) ? insight.dashboardUrl(listedDashboardId(report)) : null);

/**
 * Where Launch goes. An Insight agent opens the Insight conversation with it; so does an analyst
 * switched to the Insight runtime, with its linked agent; a shared dashboard opens its page. Without
 * AI Insight (`insight` null) every analyst opens its own dashboard, as before.
 */
export function launchUrl(report: ListedAgent, insight: AnalystInsight | null): string {
  const dashboard = dashboardPage(report, insight);
  if (dashboard) return dashboard;
  if (insight && isInsightAgentWorker(report)) {
    return isDefaultStandIn(report) ? insight.conversationUrl() : insight.conversationUrl(report.aiworker_id ?? '');
  }
  const route = analystRoute(report);
  if (insight && route.runtime === ANALYST_RUNTIMES.INSIGHT) return insight.conversationUrl(route.agentId);
  return buildReportUrl(report);
}

/** Where Edit goes: the Insight agent editor, a dashboard's page, or the analyst's Configuration tab (where its runtime is switched). */
export function editUrl(report: ListedAgent, insight: AnalystInsight | null): string {
  const dashboard = dashboardPage(report, insight);
  if (dashboard) return dashboard;
  if (insight && isInsightAgentWorker(report)) {
    return isDefaultStandIn(report) ? insight.customiseDefaultUrl : insight.editorUrl(report.aiworker_id ?? '');
  }
  return buildReportUrl(report, AnalystTabs.CONFIGURATION);
}

/** An analyst page gets the aiworker as router state; AI Insight's pages load their own. */
const navigateState = (report: ListedAgent) =>
  (isInsightAgentWorker(report) || isInsightDashboardWorker(report) ? undefined : { state: { report } });

/**
 * The stand-in has no FGA resource: anyone on this page may ask it, and whoever may create
 * analysts may customise it. Insight agents are not deleted here, as in AI Insight's own list:
 * their configuration and saved dashboards would be left behind.
 */
function useItemPermissions(report: ListedAgent) {
  const standIn = isDefaultStandIn(report);
  // The viewer's own private dashboard: no FGA resource either. Its owner opens and edits it on its page,
  // and deletes it there too (this list's Delete is for analysts).
  const ownDashboard = isPrivateDashboardItem(report);
  const permissions = useEntityResourcePermissions('analyst', standIn || ownDashboard ? undefined : report?.aiworker_id);
  if (ownDashboard) return { canRead: true, canUpdate: true, canDelete: false };
  if (standIn) return { canRead: true, canUpdate: permissions.canCreate, canDelete: false };
  if (isInsightAgentWorker(report)) return { ...permissions, canDelete: false };
  return permissions;
}

/**
 * Delete. A listed dashboard is taken off All Analysts instead: it becomes private again, so its
 * owner keeps it (AI Insight's `unpublishDashboard`), and its analyst goes with it.
 */
function useDeleteAnalystHandler(report: any, refetchAnalyst: () => void) {
  const { t } = useTranslation();
  const insight = useAnalystInsight();
  const [deleteAiWorkers, { isLoading: deleteAnalystLoading }] = useDeleteAiWorkersMutation();
  const [unpublishing, setUnpublishing] = useState(false);
  const dashboardId = listedDashboardId(report);
  const unpublish = dashboardId ? insight?.unpublishDashboard : undefined;

  const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
    if (unpublish) {
      setUnpublishing(true);
      try {
        await unpublish(dashboardId);
        CtyShowToast(checkType.success, t('{{name}} is no longer listed here. Its owner keeps the dashboard, now private.', { name: report?.name ?? '' }));
        closeModal();
        refetchAnalyst();
      } catch (error) {
        CtyShowToast(checkType.error, (error as Error)?.message || t('It could not be removed. Try again.'));
      } finally {
        setUnpublishing(false);
      }
      return;
    }
    try {
      const response = await deleteAiWorkers(report?.aiworker_id);
      CtyShowToast(checkType.success, response?.data);
      closeModal();
      refetchAnalyst();
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message || 'Delete Operation failed');
    }
  }, [dashboardId, deleteAiWorkers, refetchAnalyst, report?.aiworker_id, report?.name, t, unpublish]);

  return { handleConfirmDelete, isDeleteLoading: deleteAnalystLoading || unpublishing };
}

/**
 * The delete dialog's wording. A listed dashboard is removed, not deleted: its owner keeps it, so the
 * dialog says so and asks for no typed "delete" (sharing it again lists it again).
 */
function useDeleteWording(report: ListedAgent) {
  const { t } = useTranslation();
  if (!isInsightDashboardWorker(report)) return { confirmLabel: t('Delete Analyst') };
  return {
    confirmLabel: t('Remove'),
    menuLabel: t('Remove from All Analysts'),
    buttonLabel: t('Remove'),
    title: t('Remove from All Analysts?'),
    message: (
      <p className="text-sm">
        {t('{{name}} will no longer be listed here, and only its owner can open it. The dashboard itself is kept: its owner can share it again.', { name: report?.name ?? '' })}
      </p>
    ),
    requiresTypedConfirmation: false,
  };
}

/** What kind of analytics agent a card is, when AI Insight is on: Insight agent, or an analyst on the Insight runtime. */
function KindChips({ report, insight }: { readonly report: ListedAgent; readonly insight: AnalystInsight | null }) {
  const { t } = useTranslation();
  if (!insight) return null;
  if (isInsightDashboardWorker(report)) {
    return (
      <div className="flex flex-wrap gap-1">
        <Chip size="sm" variant="flat" color="warning">{t('Dashboard')}</Chip>
        {isPrivateDashboardItem(report) ? <Chip size="sm" variant="flat" color="default">{t('Private')}</Chip> : null}
      </div>
    );
  }
  if (isInsightAgentWorker(report)) {
    const isDefault = isDefaultStandIn(report) || (report?.code && report.code === insight.defaultAgentCode);
    return (
      <div className="flex flex-wrap gap-1">
        <Chip size="sm" variant="flat" color="secondary">{t('Insight agent')}</Chip>
        {isDefault ? <Chip size="sm" variant="flat" color="primary">{t('Default')}</Chip> : null}
      </div>
    );
  }
  if (analystRoute(report).runtime === ANALYST_RUNTIMES.INSIGHT) {
    return <Chip size="sm" variant="flat" color="success">{t('Insight runtime')}</Chip>;
  }
  return null;
}

const AnalystCardItem = ({ report, refetchAnalyst }: { readonly report: any; readonly refetchAnalyst: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const insight = useAnalystInsight();
  const { canRead, canUpdate, canDelete } = useItemPermissions(report);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteAnalystHandler(report, refetchAnalyst);
  const wording = useDeleteWording(report);

  const footerItems = (
    <>
      <CtyButton className={'w-full border-1'} variant={'bordered'} radius='sm' size={'lg'} color={'default'} isDisabled={!canUpdate} onPress={() => navigate(editUrl(report, insight), navigateState(report))}>
        {isDefaultStandIn(report) ? t('Customise') : t('Edit')}
      </CtyButton>
      <CtyButton className={'w-full'} variant={'solid'} radius='sm' size={'lg'} color={'primary'} isDisabled={!canRead} onPress={() => navigate(launchUrl(report, insight), navigateState(report))}>
        {t('Launch')}
      </CtyButton>
    </>
  );

  return (
    <EntityCardWithActions
      id={report.aiworker_id}
      name={report.name}
      description={report.description}
      bodyExtra={<KindChips report={report} insight={insight} />}
      view={report.category === categoryType.ANALYST}
      footerItems={footerItems}
      canDelete={canDelete}
      dropdownAriaLabel="Analyst Actions"
      deleteModalEntityName={report?.name ?? ''}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={wording.confirmLabel}
      deleteMenuLabel={wording.menuLabel}
      deleteModalTitle={wording.title}
      deleteModalMessage={wording.message}
      deleteRequiresTypedConfirmation={wording.requiresTypedConfirmation}
    />
  );
};

const AnalystActionsCell = ({ report, refetchAnalyst }: { readonly report: any; readonly refetchAnalyst: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const insight = useAnalystInsight();
  const { canRead, canUpdate, canDelete } = useItemPermissions(report);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteAnalystHandler(report, refetchAnalyst);
  const wording = useDeleteWording(report);

  return (
    <ListActionsCellWithDelete
      actions={[
        {
          label: isDefaultStandIn(report) ? t('Customise') : t('Edit'),
          onPress: () => navigate(editUrl(report, insight), navigateState(report)),
          variant: 'bordered',
          color: 'default',
          isDisabled: !canUpdate,
        },
        {
          label: t('Launch'),
          onPress: () => navigate(launchUrl(report, insight), navigateState(report)),
          variant: 'solid',
          color: 'primary',
          isDisabled: !canRead,
        },
      ]}
      canDelete={canDelete}
      entityName={report?.name ?? ''}
      onConfirmDelete={handleConfirmDelete}
      isDeleteLoading={isDeleteLoading}
      deleteConfirmLabel={wording.confirmLabel}
      deleteButtonLabel={wording.buttonLabel}
      deleteModalTitle={wording.title}
      deleteModalMessage={wording.message}
      deleteRequiresTypedConfirmation={wording.requiresTypedConfirmation}
    />
  );
};

const renderAnalystActionsCell = (refetchAnalyst: () => void) =>
  function AnalystActionsRenderer(params: any) {
    return <AnalystActionsCell report={params?.data} refetchAnalyst={refetchAnalyst} />;
  };

/**
 * Every analyst the user can use, for All Analysts: the analysts and, with AI Insight, the dashboards
 * shared with the tenant and the viewer's own private dashboards (AnalystInsight.privateDashboards).
 * Insight agents (sub_type insight_agent) are never listed here: they are asked from Analyst Genie.
 */
export function listedAnalyticsAgents<T extends ListedAgent>(
  reports: readonly T[],
  insight: AnalystInsight | null
): Array<T | ListedAgent> {
  // A dashboard is listed only where its page can open (AI Insight's dashboardUrl) and it names one.
  const opens = (report: T) => !isInsightDashboardWorker(report) || Boolean(insight?.dashboardUrl && listedDashboardId(report));
  const analysts = reports.filter((report) => report?.category === categoryType.ANALYST && !isInsightAgentWorker(report) && opens(report));
  return insight ? [...analysts, ...ownPrivateDashboards(insight, analysts)] : analysts;
}

/** The viewer's private dashboards that can open here and are not listed already (by their dashboard). */
function ownPrivateDashboards(insight: AnalystInsight, analysts: readonly ListedAgent[]): ListedAgent[] {
  if (!insight.dashboardUrl) return [];
  const shown = new Set(analysts.map(listedDashboardId).filter(Boolean));
  return (insight.privateDashboards ?? []).filter((item) => {
    const id = listedDashboardId(item);
    return isPrivateDashboardItem(item) && Boolean(id) && !shown.has(id);
  });
}

/* ------------------------------------------------------------------ */
/*  The Analysts page's new cards (Analyst Genie design, AI Insight on) */
/* ------------------------------------------------------------------ */

const AVATAR_COLORS = ['#E03E96', '#2563EB', '#059669', '#7C3AED', '#D97706', '#0891B2', '#DC2626', '#EA580C', '#0D9488', '#9333EA'];
const NEW_FOR_MS = 7 * 24 * 60 * 60 * 1000;

/** A card's avatar: the first letters of its first two words, or its first letter. */
export function cardInitials(name: string | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return (words[0]?.[0] ?? '?').toUpperCase();
}

/** The same colour for the same analyst, from a fixed palette. */
export function avatarColor(id: string | undefined): string {
  let hash = 0;
  for (const char of id ?? '') hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** When it last changed, as the card shows it (dd/mm/yyyy, hh:mm); '' when DOC sends no date. */
export function cardTimestamp(report: ListedAgent): string {
  const value = report.updated_at || report.created_at;
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Created in the last 7 days. */
export function isNewCard(report: ListedAgent, now = Date.now()): boolean {
  const created = report.created_at ? new Date(report.created_at).getTime() : Number.NaN;
  return !Number.isNaN(created) && now - created >= 0 && now - created < NEW_FOR_MS;
}

/** The card's ⋮ menu: one item, Delete (or Remove for a listed dashboard). */
function CardMenu({ label, onSelect }: { readonly label: string; readonly onSelect: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div ref={wrap} className="absolute right-3 top-3">
      <button
        type="button"
        aria-label={t('Analyst Actions')}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-7 w-7 items-center justify-center rounded-small text-default-400 hover:bg-default-100 hover:text-default-600"
        onClick={() => setOpen((value) => !value)}
      >
        <Icon icon="lucide:ellipsis-vertical" width={16} />
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 top-8 z-10 min-w-[140px] rounded-medium border border-default-200 bg-content1 p-1 shadow-medium">
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-small px-3 py-2 text-left text-sm font-medium text-danger hover:bg-default-100"
            onClick={() => {
              setOpen(false);
              onSelect();
            }}
          >
            <Icon icon="lucide:trash-2" width={14} />
            {label}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * One analyst in the new layout: avatar, name, last change, description, kind, and Launch / Configure.
 * Launch, Configure and Delete do what the classic card's Launch, Edit and Delete do.
 */
function AnalystGenieCard({ report, refetchAnalyst }: { readonly report: ListedAgent; readonly refetchAnalyst: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const insight = useAnalystInsight();
  const { canRead, canUpdate, canDelete } = useItemPermissions(report);
  const { handleConfirmDelete, isDeleteLoading } = useDeleteAnalystHandler(report, refetchAnalyst);
  const wording = useDeleteWording(report);
  const [confirming, setConfirming] = useState(false);
  const name = report.name ?? '';
  const when = cardTimestamp(report);
  const configureLabel = isDefaultStandIn(report) ? t('Customise') : t('Configure');

  return (
    <Card shadow="sm" className="relative h-full border border-default-200" aria-label={name}>
      <CardBody className="flex flex-col gap-2 px-[18px] pb-4 pt-5">
        {canDelete ? <CardMenu label={wording.menuLabel ?? t('Delete')} onSelect={() => setConfirming(true)} /> : null}
        <div className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-medium text-base font-bold text-white"
            style={{ background: avatarColor(report.aiworker_id) }}
          >
            {cardInitials(name)}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
            <h3 className="truncate pr-8 text-sm font-medium leading-[21px] text-foreground" title={name}>{name}</h3>
            {when ? (
              <span className="inline-flex items-center gap-1 text-xs text-default-400">
                <Icon icon="lucide:clock" width={12} />
                {when}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-end justify-between gap-2">
          <p className="line-clamp-2 flex-1 text-sm leading-normal text-default-500">
            {report.description?.trim() || t('Ready to launch or configure.')}
          </p>
          {isNewCard(report) ? <Chip size="sm" color="primary">{t('New')}</Chip> : null}
        </div>
        <KindChips report={report} insight={insight} />
        <div className="mt-auto flex items-center gap-1 border-t border-default-200 pt-3">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline disabled:cursor-default disabled:opacity-40 disabled:no-underline"
            disabled={!canRead}
            onClick={() => navigate(launchUrl(report, insight), navigateState(report))}
          >
            {t('Launch')}
            <Icon icon="lucide:arrow-up-right" width={12} />
          </button>
          <span aria-hidden="true" className="mx-1 h-4 w-px bg-default-200" />
          <button
            type="button"
            className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline disabled:cursor-default disabled:opacity-40 disabled:no-underline"
            disabled={!canUpdate}
            onClick={() => navigate(editUrl(report, insight), navigateState(report))}
          >
            {configureLabel}
            <Icon icon="lucide:square-pen" width={12} />
          </button>
        </div>
      </CardBody>
      {canDelete ? (
        <SimpleDeleteConfirmationModal
          isOpen={confirming}
          onOpenChange={setConfirming}
          entityName={name}
          title={wording.title}
          body={wording.message}
          requireTypedConfirmation={wording.requiresTypedConfirmation}
          onConfirm={() => handleConfirmDelete(() => setConfirming(false))}
          isConfirmLoading={isDeleteLoading}
          cancelLabel={t('Close')}
          confirmLabel={wording.confirmLabel}
        />
      ) : null}
    </Card>
  );
}

/** The analysts whose name contains the search, ignoring case. */
const matching = <T extends ListedAgent>(reports: readonly T[], query: string): T[] => {
  const needle = query.trim().toLowerCase();
  return needle ? reports.filter((report) => (report.name ?? '').toLowerCase().includes(needle)) : [...reports];
};

export interface AnalyticsListProps {
  /**
   * `cards`: the new Analysts page (AI Insight on): the page header holds the search, and the list is a grid of
   * the new cards with no list view. `classic` (the default, and production builds): as before.
   */
  readonly variant?: 'classic' | 'cards';
  /** The header's search, for `cards`. */
  readonly search?: string;
}

export function AnalyticsList({ variant = 'classic', search = '' }: AnalyticsListProps = {}) {
  const token = useTypedSelector((state) => state.auth.token);
  const [skip, setSkip] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);
  const { data: reports, error, isLoading, isFetching, refetch } = useGetAiWorkersByCategoryQuery({ category: categoryType.ANALYST }, { skip, refetchOnMountOrArgChange: true });
  const { t } = useTranslation();
  const insight = useAnalystInsight();
  useEffect(() => {
    if (token && skip) {
      setSkip(false);
    }
  }, [skip, token]);

  const analystReports = useMemo(
    () => listedAnalyticsAgents(reports ?? [], insight),
    [reports, insight]
  );

  const filteredReports = useMemo(
    () => matching(analystReports, variant === 'cards' ? search : searchQuery),
    [analystReports, search, searchQuery, variant]
  );

  const columnDefs = useMemo(
    () => [
      getNameColumnDef(t('Name')),
      getDescriptionColumnDef(t('Description')),
      ...(insight ? [{
        headerName: t('Runs on'),
        colId: 'runtime',
        sortable: true,
        filter: false,
        minWidth: 150,
        maxWidth: 200,
        valueGetter: (params: { data?: ListedAgent }) => (
          isInsightAgentWorker(params?.data) || isInsightDashboardWorker(params?.data)
            || analystRoute(params?.data).runtime === ANALYST_RUNTIMES.INSIGHT
            ? t('Insight runtime')
            : t('NL analyst')
        ),
      }] : []),
      getActionsColumnDef({
        headerName: t('Actions'),
        minWidth: 240,
        maxWidth: 280,
        cellRenderer: renderAnalystActionsCell(refetch),
      }),
    ],
    [t, refetch, insight]
  );

  if (variant === 'cards') {
    if ((isLoading || isFetching) && !reports) {
      return (
        <div className="flex min-h-[60vh] w-full flex-1 items-center justify-center">
          <Spinner size="lg" aria-label={t('Loading analysts')} />
        </div>
      );
    }
    if (error) return <p className="px-1 text-sm text-danger">{t('Analysts could not be loaded. Try again.')}</p>;
    if (filteredReports.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 px-6 py-[60px] text-center text-default-500">
          <Icon icon={search.trim() ? 'lucide:search-x' : 'lucide:chart-no-axes-combined'} width={36} className="opacity-40" />
          <span className="text-sm font-medium">
            {search.trim() ? t('No analysts match "{{query}}"', { query: search.trim() }) : t('No Analysts found')}
          </span>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {filteredReports.map((report) => (
          <AnalystGenieCard key={report.aiworker_id} report={report} refetchAnalyst={refetch} />
        ))}
      </div>
    );
  }

  if (analystReports.length === 0 && !isLoading && !isFetching && !error) {
    return (
      <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-4 bg-default-100">
        <div className="text-2xl text-primary">
          {t('No Analysts found')}
        </div>
      </div>
    );
  }

  return (
    <EntityListView
      items={filteredReports}
      isLoading={isLoading || isFetching}
      error={error}
      searchId="analysts-search"
      searchPlaceholder={t('Search analysts')}
      onSearch={setSearchQuery}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      emptyMessage={t('No Analysts found')}
      cardKey={(report: any) => report.aiworker_id}
      renderCard={(report: any) => (
        <AnalystCardItem report={report} refetchAnalyst={refetch} />
      )}
      tableId="analysts-list-table"
      columnDefs={columnDefs}
    />
  );
}

export default AnalyticsList;
